import React, { useEffect, useState, useCallback } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import ShareSheet from "@/components/ShareSheet";
import ClaimBox from "@/components/ClaimBox";
import VerifyBox from "@/components/VerifyBox";
import ReactionBar from "@/components/ReactionBar";
import Comments from "@/components/Comments";
import ResolutionBox from "@/components/ResolutionBox";
import ReportButton from "@/components/ReportButton";
import MakeCallForm from "@/components/MakeCallForm";
import CallCard from "@/components/CallCard";
import AdSlot from "@/components/AdSlot";
import { useAds } from "@/lib/ads";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { CATEGORY_LABELS } from "@/lib/brand";
import { fmtDateTime, deadlineLabel } from "@/lib/format";
import { Lock, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function ReceiptPage() {
  const { shortId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { slots } = useAds();
  const [call, setCall] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [stamp, setStamp] = useState(!!location.state?.justCreated);
  const [showCounter, setShowCounter] = useState(new URLSearchParams(location.search).get("counter") === "1");
  const [counters, setCounters] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");

  const load = useCallback(() => {
    api.get(`/calls/${shortId}`).then(({ data }) => setCall(data)).catch(() => setNotFound(true));
    api.get(`/calls/${shortId}/counters`).then(({ data }) => setCounters(data)).catch(() => {});
    api.get(`/calls/${shortId}/notes`).then(({ data }) => setNotes(data)).catch(() => {});
  }, [shortId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (stamp) { const t = setTimeout(() => setStamp(false), 1800); return () => clearTimeout(t); }
  }, [stamp]);
  useEffect(() => { if (call) document.title = call.prediction_text ? `“${call.prediction_text}” — Called It` : "Called It"; }, [call]);

  if (notFound) return (
    <div className="max-w-lg mx-auto px-4 py-24 text-center">
      <p className="font-display text-4xl font-bold">404</p>
      <p className="text-muted-foreground">This Call doesn't exist.</p>
      <Link to="/" className="inline-block mt-4 h-11 px-5 border-2 border-ink bg-brand text-white font-bold btn-press shadow-brutal-sm leading-[42px]">Go home</Link>
    </div>
  );
  if (!call) return <div className="max-w-lg mx-auto px-4 py-24 text-center text-muted-foreground">Loading receipt…</div>;

  const isOwner = user && call.author?.user_id === user.user_id;
  const isGuestCall = call.author?.guest;
  const called = call.status === "called_it";
  const missed = call.status === "missed_it";
  const daysEarly = call.resolution?.days_early_human;
  const author = call.author || {};

  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await api.post(`/calls/${shortId}/notes`, { text: noteText }); setNoteText(""); load(); }
    catch { toast.error("Could not add note"); }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-8 space-y-6">
      {/* Celebratory hero for resolved wins */}
      {called && (
        <div data-testid="called-it-hero" className="card-brutal card-brutal-lg bg-called p-6 text-center">
          <div className="font-display text-4xl sm:text-5xl font-bold tracking-tighter">🏆 CALLED IT.</div>
          {daysEarly && <div className="font-display text-2xl font-bold mt-2">Called {daysEarly.toUpperCase()}</div>}
        </div>
      )}
      {missed && (
        <div data-testid="missed-it-hero" className="card-brutal bg-missed text-white p-5 text-center">
          <div className="font-display text-3xl font-bold tracking-tight">MISSED IT</div>
          <p className="text-sm mt-1">This one didn't land. Receipts work both ways.</p>
        </div>
      )}

      {/* The Receipt */}
      <div data-testid="call-receipt" className="card-brutal card-brutal-lg bg-white p-6 sm:p-8 relative overflow-hidden">
        {stamp && (
          <div className="absolute top-3 left-0 right-0 flex items-center justify-center z-20 pointer-events-none">
            <div className="animate-stamp-in border-4 border-ink bg-locked px-6 py-3 font-display text-2xl sm:text-3xl font-bold tracking-tight shadow-brutal-lg">
              🔒 IT'S ON THE RECORD.
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-5">
          <span className="flex items-center gap-1.5 font-mono text-xs font-bold uppercase tracking-widest">
            <Lock className="w-3.5 h-3.5" strokeWidth={3} /> On the Record
          </span>
          <StatusBadge status={call.status} />
        </div>

        {call.sealed && !call.sealed_revealed ? (
          <div className="text-center py-6">
            <div className="font-display text-3xl font-bold">🔐 SEALED CALL</div>
            <p className="text-sm text-muted-foreground mt-2">
              {author.display_name} locked a prediction on<br />
              <b>{fmtDateTime(call.locked_at)}</b>
            </p>
            <p className="text-sm mt-3">Reveal date: <b>{fmtDateTime(call.sealed_reveal_at)}</b></p>
          </div>
        ) : (
          <p className="font-display text-2xl sm:text-3xl font-bold leading-tight tracking-tight">“{call.prediction_text}”</p>
        )}

        <div className="dashed-tear my-5" />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10 border-2 border-ink">
              <AvatarImage src={author.avatar_url} />
              <AvatarFallback className="bg-locked font-bold">{(author.display_name || "A")[0]}</AvatarFallback>
            </Avatar>
            <div className="leading-tight">
              <div className="font-bold">
                {author.username ? <Link to={`/@${author.username}`} className="hover:underline">{author.display_name}</Link> : author.display_name}
                {author.guest && <span className="text-muted-foreground font-normal"> · Guest</span>}
              </div>
              {author.username && <div className="text-xs text-muted-foreground font-mono">@{author.username}</div>}
              {author.guest && <div className="text-xs text-muted-foreground">Unverified guest</div>}
            </div>
          </div>
          {call.confidence != null && (
            <div className="text-right">
              <div className="font-mono text-2xl font-bold">{call.confidence}%</div>
              <div className="text-xs text-muted-foreground">confident</div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-5 font-mono text-xs">
          <div className="border-2 border-ink p-2">
            <div className="text-muted-foreground">LOCKED</div>
            <div className="font-bold">{fmtDateTime(call.locked_at)}</div>
          </div>
          <div className="border-2 border-ink p-2">
            <div className="text-muted-foreground">DEADLINE</div>
            <div className="font-bold">{deadlineLabel(call.deadline_type, call.deadline_value)}</div>
          </div>
          {call.category && (
            <div className="border-2 border-ink p-2">
              <div className="text-muted-foreground">CATEGORY</div>
              <div className="font-bold">{CATEGORY_LABELS[call.category] || call.category}</div>
            </div>
          )}
          <div className="border-2 border-ink p-2">
            <div className="text-muted-foreground">CALL ID</div>
            <div className="font-bold">{call.short_id}</div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mt-4 text-center italic">Original Call — never edited</p>
      </div>

      <button data-testid="share-my-call-btn" onClick={() => setShareOpen(true)}
        className="w-full h-14 border-2 border-ink bg-brand text-white font-display font-bold text-xl tracking-wide btn-press shadow-brutal flex items-center justify-center gap-2">
        <Share2 className="w-5 h-5" strokeWidth={2.5} /> Share My Call
      </button>

      <ClaimBox shortId={shortId} isGuestCall={isGuestCall} onClaimed={load} />

      <VerifyBox shortId={shortId} />

      <ReactionBar shortId={shortId} initial={call.reactions} />

      <ResolutionBox call={call} onChange={load} />

      <AdSlot slot={slots.receipt} className="mt-2" />

      {/* Notes (append-only) */}
      {(notes.length > 0 || isOwner) && (
        <div className="card-brutal p-5 bg-white" data-testid="notes-section">
          <h3 className="font-display text-lg font-bold mb-2">Notes</h3>
          {notes.map((n) => (
            <div key={n.id} className="border-l-4 border-ink pl-3 py-1 mb-2">
              <p className="text-sm">{n.text}</p>
              <p className="text-xs text-muted-foreground font-mono">{fmtDateTime(n.created_at)}</p>
            </div>
          ))}
          {isOwner && (
            <div className="flex gap-2 mt-2">
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={1}
                placeholder="Add a note (won't change the original Call)…" className="border-2 border-ink resize-none" data-testid="note-input" />
              <button onClick={addNote} data-testid="add-note-btn" className="px-3 border-2 border-ink bg-white font-bold btn-press shadow-brutal-sm">Add</button>
            </div>
          )}
        </div>
      )}

      {/* Counter-Calls */}
      <div className="card-brutal p-5 bg-locked" data-testid="counter-section">
        <h3 className="font-display text-2xl font-bold">Think they're wrong?</h3>
        <p className="text-sm mb-3">Make your Call. Agree? Put your own confidence on the record.</p>
        <button data-testid="toggle-counter-btn" onClick={() => setShowCounter(!showCounter)}
          className="h-11 px-5 border-2 border-ink bg-ink text-white font-bold btn-press shadow-brutal-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> {showCounter ? "Close" : "Make your Call"}
        </button>
      </div>
      {showCounter && <MakeCallForm counterOf={shortId} />}
      {counters.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-display text-xl font-bold">Counter-Calls ({counters.length})</h3>
          {counters.map((c) => <CallCard key={c.short_id} call={c} />)}
        </div>
      )}

      <Comments shortId={shortId} />

      <div className="flex justify-end pt-2"><ReportButton targetType="call" targetId={shortId} /></div>

      <ShareSheet open={shareOpen} onOpenChange={setShareOpen} shortId={shortId}
        predictionText={call.prediction_text} authorName={author.display_name} />
    </div>
  );
}
